import {
  BarColor,
  Box,
  Card,
  TagColor as Color,
  Dimensions,
  Icon,
  type MediumIcon,
  Skeleton,
  StackedBar,
  Tag,
  Text,
  Tooltip,
  useToggle,
} from '@factorialco/deprecated-design-system'

import i18n from 'modules/core/lib/i18n'

import { formatProjectHours } from 'modules/project_management/lib/formatHours'

import { Amount } from '../Amount'

type InsightCardProps = React.PropsWithChildren<{
  showOverageTag?: boolean
  title?: string
  titleGap?: Dimensions
  info?: string
  icon?: MediumIcon
}>

export const InsightCard = ({
  children,
  showOverageTag,
  title,
  info,
  icon,
  titleGap = 's16',
}: InsightCardProps) => {
  const [toggle, setToggle] = useToggle(false)

  return (
    <Card>
      <Card.Content>
        <Box width="full" flexDirection="row" flexGrow>
          <Box
            inline
            flexDirection="column"
            justifyContent="flexStart"
            width="full"
            gap={titleGap}
            paddingBottom="s6"
          >
            <Box flexDirection="row" justifyContent={icon ? 'flexStart' : 'spaceBetween'}>
              {icon && <Icon.Medium icon={icon} />}
              <Text color="grey800" size="300" ellipsis whiteSpace="nowrap">
                {title}
              </Text>
              {showOverageTag && (
                <Tag leftItem="circle" inline capitalize showCircle type={Color.radical}>
                  <Text color="grey800" size="100" weight="semibold" whiteSpace="nowrap">
                    {i18n.t('project_management.insights.tags.overage')}
                  </Text>
                </Tag>
              )}
              {info && (
                <Tooltip.Alt open={toggle} toggle={setToggle} attachment="top center" label={info}>
                  <Box width="s12">
                    <Icon.Medium icon="info-circle" />
                  </Box>
                </Tooltip.Alt>
              )}
            </Box>
            {children}
          </Box>
        </Box>
      </Card.Content>
    </Card>
  )
}

type HoursInsightCardProps = InsightCardProps & {
  imputedMinutes: number
}

function Hours({
  icon,
  imputedMinutes,
  title = i18n.t('project_management.details.total_hours'),
  titleGap,
}: HoursInsightCardProps) {
  return (
    <InsightCard title={title} icon={icon} titleGap={titleGap}>
      <Text.Header2>{formatProjectHours(imputedMinutes)}</Text.Header2>
    </InsightCard>
  )
}

type CostInsightCardProps = InsightCardProps & {
  totalCost: number
  currency: string
}

function Cost({
  icon,
  currency,
  title = i18n.t('project_management.details.total_cost'),
  totalCost,
  info,
  titleGap,
}: CostInsightCardProps) {
  return (
    <InsightCard title={title} info={info} icon={icon} titleGap={titleGap}>
      <Amount
        amount={totalCost}
        currency={currency}
        size="400"
        weight="semibold"
        color="grey1000"
      />
    </InsightCard>
  )
}

type CounterInsightCardProps = InsightCardProps & {
  totalCount: number
}

function Counter({ title, totalCount }: CounterInsightCardProps) {
  return (
    <InsightCard title={title} titleGap="s0">
      <Text color="grey1000" size="600" weight="medium" ellipsis whiteSpace="nowrap">
        {totalCount}
      </Text>
    </InsightCard>
  )
}

interface StackBarProps {
  label: number
  value: number
  color: BarColor
}

type StackedBarProps = InsightCardProps & {
  totalAmount: number
  stackBars: StackBarProps[]
}

function StackedProgressBar({ title, stackBars, totalAmount }: StackedBarProps) {
  return (
    <InsightCard title={title} titleGap="s0">
      <Text weight="semibold" size="500" color="grey1000">
        {totalAmount}
      </Text>
      <StackedBar>
        {stackBars.map(({ label, value, color }) => (
          <StackedBar.Bar key={value} label={label} value={value} color={color} />
        ))}
      </StackedBar>
    </InsightCard>
  )
}

type SkeletonInsightCardProps = InsightCardProps & {
  withStackedBar?: boolean
}

function SkeletonCard({ title, withStackedBar }: SkeletonInsightCardProps) {
  return (
    <InsightCard title={title} titleGap="s0">
      <Box paddingTop="s24">
        <Skeleton width="s48" height="s8" isLoading />
      </Box>
      {withStackedBar && (
        <Box paddingTop="s40">
          <Skeleton width="s200" height="s8" isLoading />
        </Box>
      )}
    </InsightCard>
  )
}

InsightCard.Hours = Hours
InsightCard.Cost = Cost
InsightCard.Counter = Counter
InsightCard.StackedProgressBar = StackedProgressBar
InsightCard.Skeleton = SkeletonCard
